import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Skeleton } from "components/ui/skeleton";
import { Spinner } from "components/ui/spinner";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import {
  fetchBatchesAdditionalProperties,
  fetchCredits,
} from "data/queries-common";
import { CheckedItems, OutputFormat } from "pages/geoscape-batch/models";
import {
  BatchConfigProps,
  InputBatchConfigFormat,
  StepperControlProps,
} from "pages/geoscape-batch/types";
import {
  batchOutputGeocode,
  batchOutputReverseGeocode,
} from "pages/geoscape-batch/utils";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Md5 } from "ts-md5";
import { resolveIdentityId } from "utils/identity";
import { Accordion } from "components/ui/accordion";
import { AccordionComponent } from "./accordion";
import MappingTableEnrichment from "./mapping-table-enrichment";
import MappingTableInput from "./mapping-table-input";
import { addIndex } from "./utils";

interface DefineOutputFileMappingsProps {}

const mapAdditionalProperties: { [key: string]: string } = {
  localGovernmentArea: "localGovernmentArea",
  asgsMain2011: "asgsMain",
  asgsMain2016: "asgsMain",
  asgsMain2021: "asgsMain",
  asgsMain2026: "asgsMain",
  asgsRemoteness2016: "asgsRemoteness",
  asgsRemoteness2011: "asgsRemoteness",
  stateElectorate: "stateElectorate",
  solarPanelBundle: "solarPanelBundle",
  asgsRemoteness2021: "asgsRemoteness",
  seifa2011: "seifa",
  seifa2016: "seifa",
  seifa2021: "seifa",
  commonwealthElectorate: "commonwealthElectorate",
  buildings: "buildings",
};

export const DefineOutputFileMappings = (
  props: DefineOutputFileMappingsProps & BatchConfigProps & StepperControlProps,
) => {
  const allCheckedItems = props.batchConfig.hubInfo?.checkedItems;
  const [identityState] = useIdentity();

  const identityId = resolveIdentityId(identityState);
  const queryClient = useQueryClient();

  const [inPutParameters, setInPutParameters] = useState<
    InputBatchConfigFormat[]
  >(props.batchConfig.hubInfo.csvHeaders);

  const definitionsQuery = useQuery({
    queryKey: [QueryKeys.batchesAdditionalPropertiesKey, identityId],
    queryFn: () => fetchBatchesAdditionalProperties(),
  });

  const isLoading = definitionsQuery.isPending;

  const [checkedItems, setCheckedItems] = useState<CheckedItems>({});

  const [enrichmentRows, setEnrichmentRows] = useState<OutputFormat[]>([]);

  useEffect(() => {
    setCheckedItems(props.batchConfig.hubInfo.checkedItems);

    setEnrichmentRows(
      addIndex(
        props.batchConfig.output.format.length > 0
          ? props.batchConfig.output.format
          : props.batchConfig.type == "addressGeocoder"
            ? batchOutputGeocode.output
            : batchOutputReverseGeocode.output,
      ) || [],
    );
  }, []);

  const updateBatchConfig = () => {
    const additionalProperties = [];

    for (const [key, value] of Object.entries(checkedItems)) {
      if (Object.values(value).some(Boolean)) {
        const propertyName = mapAdditionalProperties[key];
        if (propertyName) {
          additionalProperties.push(propertyName);
        }
      }
    }

    const batchConfigHash = Md5.hashStr(
      JSON.stringify(props.batchConfig.output.format) +
        JSON.stringify(props.batchConfig.hubInfo.checkedItems),
    );

    const newConfig = {
      ...props.batchConfig,
      output: {
        ...props.batchConfig.output,
        format: enrichmentRows,
      },
      hubInfo: {
        ...props.batchConfig.hubInfo,
        checkedItems: checkedItems,
        csvHeaders: inPutParameters,
        batchConfigHashOutput: batchConfigHash,
      },
      parameters: {
        ...props.batchConfig.parameters,
        additionalProperties: additionalProperties,
      },
    };

    props.setBatchConfig(newConfig);

    return newConfig;
  };

  useQuery({
    queryKey: [QueryKeys.credits, identityId],
    queryFn: () => fetchCredits(),
  });

  return (
    <div className="flex flex-col gap-4">
      <ReactQueryErrorWrapper queries={[definitionsQuery]} mutations={[]} />
      <div className="flex flex-col lg:flex-row gap-4">
        <Card className="flex-1 min-w-0 lg:basis-[55%]">
          <CardHeader>
            <CardTitle>Step 4: Output - File Mapping</CardTitle>
            <CardDescription>
              Review the arrangement of your processed data. The 'Column Name'
              displays your file's column names and Attribute displays the
              attribute name from the request and response.
            </CardDescription>
          </CardHeader>

          {enrichmentRows && (
            <CardContent>
              <h3 className="text-base font-medium mb-2">From Input File</h3>
              <MappingTableInput
                inPutParameters={inPutParameters}
                setInPutParameters={setInPutParameters}
              />
              <h3 className="text-base font-medium mb-2 mt-4">Enrichment</h3>
              <MappingTableEnrichment
                props={props}
                setCheckedItems={setCheckedItems}
                enrichmentRows={enrichmentRows}
                setEnrichmentRows={setEnrichmentRows}
              />
            </CardContent>
          )}
        </Card>

        <Card className="flex-1 min-w-0 lg:basis-[45%]">
          <CardHeader>
            <CardTitle>Enrichment</CardTitle>
            <CardDescription>
              Enhance Your Job with Additional Information and Insights
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading && <Skeleton className="h-60 w-full" />}
            {definitionsQuery?.data && (
              <Accordion type="multiple">
                {Object.keys(definitionsQuery.data).map((key) => {
                  const isAddressGeocoderAndRelevantKey =
                    props.batchConfig.type === "addressGeocoder" &&
                    (key === "matchingMetadataReverseGeocode" ||
                      key === "queryMetadataReverseGeocode");

                  const isReverseGeocoderAndRelevantKey =
                    props.batchConfig.type === "reverseGeocoder" &&
                    (key === "matchingMetadataGeocode" ||
                      key === "queryMetadataGeocode");

                  if (
                    isAddressGeocoderAndRelevantKey ||
                    isReverseGeocoderAndRelevantKey
                  ) {
                    return null;
                  }

                  return (
                    <AccordionComponent
                      key={key}
                      batchConfig={props.batchConfig}
                      outPutFormatDefinition={definitionsQuery.data[key]}
                      propertyCheckedItems={allCheckedItems[key]}
                      title={key}
                      setBatchConfig={props.setBatchConfig}
                      checkedItems={checkedItems}
                      setCheckedItems={setCheckedItems}
                      enrichmentRows={enrichmentRows}
                      setEnrichmentRows={setEnrichmentRows}
                    />
                  );
                })}
              </Accordion>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-row justify-end gap-4">
        <Button
          variant="outline"
          onClick={() => props.setActiveStep(Math.max(props.activeStep - 1, 0))}
        >
          Back
        </Button>

        <Button
          onClick={() => {
            queryClient.removeQueries({
              queryKey: [QueryKeys.batchesQuotedBatchConfig, identityId],
            });

            const newConfig = updateBatchConfig();

            props.saveBatchConfig(
              newConfig,
              Math.min(props.activeStep + 1, props.maxSteps),
            );
          }}
          disabled={props.isSaveBatchConfigLoading}
        >
          {props.isSaveBatchConfigLoading && <Spinner />}
          Continue
        </Button>
      </div>
    </div>
  );
};
